/* eslint-disable complexity */
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';

import { formatCurrency } from '@futrli/localisation';
import { PerformanceMetricsBusinessInsightsComparisonData } from '@futrli-sdk/predict';

import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { PerformanceMetricCard } from './performance-metric-card';
import * as styles from './performance-overview.scss';

import { PRIOR_PERIOD_VALUE } from './show-performance-select';

interface PerformanceCardsRowProps {
  periodData?: PerformanceMetricsBusinessInsightsComparisonData;
  comparisonMode: string;
  isLoading: boolean;
}

export const PerformanceCardsRow = ({ periodData, comparisonMode, isLoading }: PerformanceCardsRowProps) => {
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();

  const isPriorPeriod = comparisonMode === PRIOR_PERIOD_VALUE;
  const priorPeriodSuffix = intl.formatMessage({
    id: 'predict.performance_overview.vs_prior_period',
    defaultMessage: 'vs prior period'
  });
  const aboveTargetSuffix = intl.formatMessage({
    id: 'predict.performance_overview.above_target',
    defaultMessage: 'above target'
  });
  const underTargetSuffix = intl.formatMessage({
    id: 'predict.performance_overview.under_target',
    defaultMessage: 'under target'
  });

  const getTargetSuffix = (variance: string | undefined, isPositive: boolean) => {
    if (isPriorPeriod) {
      return priorPeriodSuffix;
    }
    const num = Number(variance);
    if (num === 0) {
      return undefined;
    }
    return isPositive ? aboveTargetSuffix : underTargetSuffix;
  };

  const showLoading = isLoading;
  const noData = !periodData && !isLoading;
  const emptyValue = '-';

  const fmt = (val: string | number) => formatCurrency(Number(val), intl, currency || '', 0, 0);

  const fmtVariance = (variance: string) => {
    const num = Number(variance);
    const prefix = num > 0 ? '+' : '';
    return `${prefix}${fmt(num)}`;
  };

  return (
    <div className={styles.cardsRow}>
      <PerformanceMetricCard
        label={intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' })}
        value={periodData ? fmt(periodData.sales.value) : emptyValue}
        variance={noData ? undefined : periodData?.sales.variance}
        varianceValue={periodData ? fmtVariance(periodData.sales.variance) : undefined}
        varianceSuffix={getTargetSuffix(periodData?.sales.variance, Number(periodData?.sales.variance) > 0)}
        isPositiveVariance={Number(periodData?.sales.variance) > 0}
        isLoading={showLoading}
      />
      <PerformanceMetricCard
        label={intl.formatMessage({ id: 'predict.predictions.costs', defaultMessage: 'Costs' })}
        value={periodData ? fmt(periodData.cost_of_sales.value) : emptyValue}
        variance={noData ? undefined : periodData?.cost_of_sales.variance}
        varianceValue={periodData ? fmtVariance(periodData.cost_of_sales.variance) : undefined}
        varianceSuffix={getTargetSuffix(
          periodData?.cost_of_sales.variance,
          Number(periodData?.cost_of_sales.variance) < 0
        )}
        isPositiveVariance={Number(periodData?.cost_of_sales.variance) < 0}
        isLoading={showLoading}
      />
      <PerformanceMetricCard
        label={intl.formatMessage({ id: 'financial_fields.category.expenses', defaultMessage: 'Expenses' })}
        value={periodData ? fmt(periodData.expenses.value) : emptyValue}
        variance={noData ? undefined : periodData?.expenses.variance}
        varianceValue={periodData ? fmtVariance(periodData.expenses.variance) : undefined}
        varianceSuffix={getTargetSuffix(periodData?.expenses.variance, Number(periodData?.expenses.variance) < 0)}
        isPositiveVariance={Number(periodData?.expenses.variance) < 0}
        isLoading={showLoading}
      />
      <PerformanceMetricCard
        label={intl.formatMessage({ id: 'predict.net_profit', defaultMessage: 'Net profit' })}
        value={periodData ? fmt(periodData.net_profit.value) : emptyValue}
        variance={noData ? undefined : periodData?.net_profit.variance}
        varianceValue={periodData ? fmtVariance(periodData.net_profit.variance) : undefined}
        varianceSuffix={getTargetSuffix(periodData?.net_profit.variance, Number(periodData?.net_profit.variance) > 0)}
        isPositiveVariance={Number(periodData?.net_profit.variance) > 0}
        isLoading={showLoading}
      />
      <PerformanceMetricCard
        label={intl.formatMessage({
          id: 'predict.performance_overview.available_cash',
          defaultMessage: 'Available cash'
        })}
        value={periodData ? fmt(isPriorPeriod ? periodData.cash.value : periodData.cash.comparison_value) : emptyValue}
        isLoading={showLoading}
        style={{ justifyContent: 'flex-start' }}
      />
    </div>
  );
};
